import React from 'react'
import { SearchMessagePairsQuery } from './queries.generated'
import { OracleSidebarItem } from './OracleSidebarItem'
import { Location } from './history-types'
import { InternationalStrings, useTranslatedText } from '../OracleCommons/StaticTextContext'

interface OracleSearchResultsProps {
  searchData: SearchMessagePairsQuery | undefined
  loading: boolean
  onItemSelect: (chatSessionId: string) => void
  onSnippetSelect: (
    chatSessionId: string,
    messagePairId: string,
    messageIndexInConversation: number,
    snippet: string,
    matchSource: string,
  ) => void
  location: Location
  staticText: InternationalStrings
  personalityMap: Map<string, string> | Record<string, string>
  currentChatSessionId?: string
  favoriteConversationIds: Set<string>
  selectedSnippetId?: string
  onContextMenu: (chatSessionId: string, triggerElement: HTMLElement, isFavorite: boolean) => void
  contextMenu: {
    isVisible: boolean
    chatSessionId: string | null
  }
  renameState: {
    isRenaming: boolean
    renamingSessionId: string | null
    newName: string
  }
  onStartRename: (sessionId: string, currentName: string) => void
  onCancelRename: () => void
  onSaveRename: () => void
  multipleSearchResultsEnabled?: boolean
}

export const OracleSearchResults: React.FC<OracleSearchResultsProps> = ({
  searchData,
  loading,
  onItemSelect,
  onSnippetSelect,
  location,
  staticText,
  personalityMap,
  currentChatSessionId,
  favoriteConversationIds,
  onContextMenu,
  contextMenu,
  renameState,
  onStartRename,
  onCancelRename,
  onSaveRename,
  selectedSnippetId,
  multipleSearchResultsEnabled = false,
}) => {
  const searchingText = useTranslatedText({ keys: ['searchResults', 'searching'] })
  const noResultsText = useTranslatedText({ keys: ['searchResults', 'noResults'] })
  const favoriteConversationsLabel = useTranslatedText({ keys: ['searchResults', 'favoriteConversations'] })
  const conversationsLabel = useTranslatedText({ keys: ['searchResults', 'conversations'] })

  if (loading) {
    return <div className="oracle-search-results__loading">{searchingText}</div>
  }

  if (!searchData?.searchMessagePairs?.conversations?.length) {
    return (
      <div className="oracle-search-results__empty">
        <p>{noResultsText}</p>
      </div>
    )
  }

  // Separate favorites from regular conversations
  const favorites = []
  const regular = []

  searchData.searchMessagePairs.conversations.forEach((conv) => {
    if (favoriteConversationIds.has(conv.conversationId)) {
      favorites.push(conv)
    } else {
      regular.push(conv)
    }
  })

  return (
    <div className="oracle-search-results">
      {favorites.length > 0 && (
        <div className="oracle-search-results__section">
          <div className="oracle-search-results__label">
            {favoriteConversationsLabel}
          </div>
          {favorites.map((conv) => (
            <div key={conv.conversationId} className="oracle-search-result">
              <OracleSidebarItem
                title={conv.conversationTitle}
                titleHtml={conv.highlightedTitle}
                isSelected={currentChatSessionId === conv.conversationId && !selectedSnippetId}
                isContextMenuActive={
                  contextMenu.isVisible && contextMenu.chatSessionId === conv.conversationId
                }
                onSelect={() => onItemSelect(conv.conversationId)}
                onContextMenu={(triggerElement) => {
                  onContextMenu(conv.conversationId, triggerElement, true)
                }}
                location={location}
                chatSessionId={conv.conversationId}
                personalityId={
                  personalityMap instanceof Map
                    ? personalityMap.get(conv.conversationId)
                    : personalityMap[conv.conversationId]
                }
                staticText={staticText}
                renameState={renameState}
                onStartRename={onStartRename}
                onCancelRename={onCancelRename}
                onSaveRename={onSaveRename}
              />
              {conv.matchedMessages?.length > 0 && (() => {
                // Old behavior: show only the last message, non-clickable
                if (!multipleSearchResultsEnabled) {
                  const lastMessage = conv.matchedMessages[conv.matchedMessages.length - 1]
                  return (
                    <div className="oracle-search-result__snippet">
                      <div
                        className="oracle-search-result__message oracle-search-result__message--read-only"
                        dangerouslySetInnerHTML={{ __html: lastMessage.snippet }}
                      />
                    </div>
                  )
                }
                // New behavior: show all messages, clickable
                return conv.matchedMessages
                  .slice()
                  .sort((a, b) => a.messageIndexInConversation - b.messageIndexInConversation)
                  .map((m) => (
                    <div key={`${m.messagePairId}-${m.matchSource}`} className="oracle-search-result__snippet">
                      <div
                        className={`oracle-search-result__message${selectedSnippetId === m.messagePairId ? ' oracle-search-result__message--selected' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => onSnippetSelect(
                          conv.conversationId,
                          m.messagePairId,
                          m.messageIndexInConversation,
                          m.snippet,
                          m.matchSource,
                        )}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onSnippetSelect(
                              conv.conversationId,
                              m.messagePairId,
                              m.messageIndexInConversation,
                              m.snippet,
                              m.matchSource,
                            )
                          }
                        }}
                        dangerouslySetInnerHTML={{ __html: m.snippet }}
                      />
                    </div>
                  ))
              })()}
            </div>
          ))}
        </div>
      )}

      {regular.length > 0 && (
        <div className="oracle-search-results__section">
          <div className="oracle-search-results__label">
            {conversationsLabel}
          </div>
          {regular.map((conv) => (
            <div key={conv.conversationId} className="oracle-search-result">
              <OracleSidebarItem
                title={conv.conversationTitle}
                titleHtml={conv.highlightedTitle}
                isSelected={currentChatSessionId === conv.conversationId && !selectedSnippetId}
                isContextMenuActive={
                  contextMenu.isVisible && contextMenu.chatSessionId === conv.conversationId
                }
                onSelect={() => onItemSelect(conv.conversationId)}
                onContextMenu={(triggerElement) => {
                  onContextMenu(conv.conversationId, triggerElement, false)
                }}
                location={location}
                chatSessionId={conv.conversationId}
                personalityId={
                  personalityMap instanceof Map
                    ? personalityMap.get(conv.conversationId)
                    : personalityMap[conv.conversationId]
                }
                staticText={staticText}
                renameState={renameState}
                onStartRename={onStartRename}
                onCancelRename={onCancelRename}
                onSaveRename={onSaveRename}
              />
              {conv.matchedMessages?.length > 0 && (() => {
                // Old behavior: show only the last message, non-clickable
                if (!multipleSearchResultsEnabled) {
                  const lastMessage = conv.matchedMessages[conv.matchedMessages.length - 1]
                  return (
                    <div className="oracle-search-result__snippet">
                      <div
                        className="oracle-search-result__message oracle-search-result__message--read-only"
                        dangerouslySetInnerHTML={{ __html: lastMessage.snippet }}
                      />
                    </div>
                  )
                }
                // New behavior: show all messages, clickable
                return conv.matchedMessages
                  .slice()
                  .sort((a, b) => a.messageIndexInConversation - b.messageIndexInConversation)
                  .map((m) => (
                    <div key={`${m.messagePairId}-${m.matchSource}`} className="oracle-search-result__snippet">
                      <div
                        className={`oracle-search-result__message${selectedSnippetId === m.messagePairId ? ' oracle-search-result__message--selected' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => onSnippetSelect(
                          conv.conversationId,
                          m.messagePairId,
                          m.messageIndexInConversation,
                          m.snippet,
                          m.matchSource,
                        )}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onSnippetSelect(
                              conv.conversationId,
                              m.messagePairId,
                              m.messageIndexInConversation,
                              m.snippet,
                              m.matchSource,
                            )
                          }
                        }}
                        dangerouslySetInnerHTML={{ __html: m.snippet }}
                      />
                    </div>
                  ))
              })()}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
